import React, { useEffect } from 'react';
import { Order } from '../types';

interface PaymentVerificationModalProps {
  payment: Order;
  imageUrls: Record<string, string>;
  onClose: () => void;
  onVerify: (orderId: string) => void;
  onReject: (orderId: string, reason: string) => void;
  onFetchImage: (filename: string) => void;
}

export const PaymentVerificationModal: React.FC<PaymentVerificationModalProps> = ({
  payment,
  imageUrls,
  onClose,
  onVerify,
  onReject,
  onFetchImage
}) => {
  useEffect(() => {
    // Load images when payment is selected
    if (payment.manualPayment?.evidenceFiles) {
      payment.manualPayment.evidenceFiles.forEach(file => {
        if (!imageUrls[file.filename]) {
          onFetchImage(file.filename);
        }
      });
    }
  }, [payment]);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '15px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
          color: 'white',
          borderRadius: '15px 15px 0 0'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.3rem' }}>
            Manual Payment Verification - #{payment._id.slice(-6)}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            ✕
          </button>
        </div>

        {/* Modal Content */}
        <div style={{ padding: '2rem' }}>
          {/* Customer Info */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#333' }}>
              Customer Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <strong>Name:</strong> {payment.customerName}
              </div>
              <div>
                <strong>Email:</strong> {payment.customerEmail}
              </div>
              <div>
                <strong>Phone:</strong> {payment.customerPhone || 'Not provided'}
              </div>
              <div>
                <strong>Country:</strong> {payment.customerCountry}
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#333' }}>
              Payment Details
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <strong>Method:</strong> 🇨🇴 {payment.paymentMethod?.replace('manual_', '').replace('_', ' ')}
              </div>
              <div>
                <strong>Amount:</strong> ${((payment.amount || 0) / 100).toFixed(2)} {payment.currency}
              </div>
              <div>
                <strong>Trees:</strong> {payment.treesOrdered}
              </div>
              <div>
                <strong>Date:</strong> {new Date(payment.createdAt).toLocaleString()}
              </div>
              <div>
                <strong>Reference:</strong> {payment.manualPayment?.referenceNumber || 'Not provided'}
              </div>
              <div>
                <strong>Status:</strong> {payment.manualPayment?.verificationStatus}
              </div>
            </div>
          </div>

          {/* Payment Evidence */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1rem', color: '#333' }}>
              Payment Evidence
            </h3>
            {payment.manualPayment?.evidenceFiles && payment.manualPayment.evidenceFiles.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                {payment.manualPayment.evidenceFiles.map((file: any, index: number) => (
                  <div key={index} style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#f9f9f9'
                  }}>
                    {imageUrls[file.filename] ? (
                      <img
                        src={imageUrls[file.filename]}
                        alt={`Payment evidence ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover',
                          cursor: 'pointer'
                        }}
                        onClick={() => window.open(imageUrls[file.filename], '_blank')}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f0f0f0',
                        color: '#666'
                      }}>
                        Loading image...
                      </div>
                    )}
                    <div style={{ padding: '0.5rem' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                        {file.originalName}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {(file.fileSize / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666', background: '#f9f9f9', borderRadius: '8px' }}>
                No payment evidence uploaded
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to reject this payment?')) {
                  const reason = window.prompt('Reason for rejection:');
                  if (reason) {
                    onReject(payment._id, reason);
                  }
                }
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              Reject Payment
            </button>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to approve this payment? This will move it to the main orders section.')) {
                  onVerify(payment._id);
                }
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              Approve Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
